import _ from "lodash";
/** external */
import React, { useCallback, useEffect, useRef, memo, useMemo } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import classNames from "classnames";
/** internal */
import {
  createQuestion,
  disableScrollEvent,
} from "Worksheet/modules/WorksheetModules";
import { useI18n } from "Hooks";
import { scrollIntoView, isElementInViewPortVertical } from "Utils";

/** relative */
import {
  getFormattedSectionFeedDetails,
  isStudentEndMode,
} from "../../modules/WorksheetUtils";
import { getFormattedSectionFeedDetailsV2 } from "./SectionFeedUtils";
import {
  WORKSHEET_MODES,
  MODE_TEACHER_VIEW,
  MODE_TEACHER_CREATE,
  ORG_RESOURCE_TYPE_WORKSHEET,
  RESOURCE_TYPE_QUESTION,
  FIELD_UID_TITLE,
} from "../../modules/WorksheetConstants";
import SectionHeader from "./SectionHeader";
import SectionDetails from "./SectionDetails";
import classes from "./SectionFeed.scss";

const Section = memo(props => {
  const {
    sectionId,
    containerClasses,
    sectionLabel,
    mode,
    isFromLibrary,
    questions,
    addQuestion,
    setQuestionRef,
    sections,
    portalType,
    showWorksheetAssistant,
    activeQuestion,
    disableScrollEvent,
    sectionScore,
    sectionMarks,
    isStudentWorkbookForceViewMode,
    assignmentId,
    studentAssignmentId,
    isStudentRevamp,
    isLockScreenEnabled,
  } = props;

  const sectionContainerRef = useRef(null);

  useEffect(() => {
    // Scroll to the question when its selected
    if (
      sectionId == activeQuestion &&
      sectionContainerRef?.current &&
      !isElementInViewPortVertical({
        element: sectionContainerRef?.current,
      })
    ) {
      disableScrollEvent(true);
      scrollIntoView({
        element: sectionContainerRef.current,
        block: "start",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeQuestion]);

  return (
    <div key={sectionId} className={containerClasses} ref={sectionContainerRef}>
      {/* <div className={containerClasses} ref={fieldRefs}></div> */}
      <SectionHeader
        id={sectionId}
        label={sectionLabel}
        mode={mode}
        isFromLibrary={isFromLibrary}
        sectionScore={sectionScore}
        sectionMarks={sectionMarks}
        isStudentRevamp={isStudentRevamp}
      />
      <SectionDetails
        sectionId={sectionId}
        questions={questions}
        mode={mode}
        setQuestionRef={setQuestionRef}
        isLastSection={sectionId === _.get(_.last(sections), "id")}
        addQuestion={addQuestion}
        isFromLibrary={isFromLibrary}
        portalType={portalType}
        showWorksheetAssistant={showWorksheetAssistant}
        isStudentWorkbookForceViewMode={isStudentWorkbookForceViewMode}
        assignmentId={assignmentId}
        studentAssignmentId={studentAssignmentId}
        isStudentRevamp={isStudentRevamp}
        isLockScreenEnabled={isLockScreenEnabled}
      />
    </div>
  );
});

Section.displayName = "Section";

/** This component renders all the sections and its child questions  */
const SectionFeed = memo(props => {
  const {
    sections,
    mode,
    submissions,
    isEvaluationApplicable,
    isSharedWithStudent,
    isStudentEvaluationEnabled,
    isTeacherEvaluationEnabled,
    isStudentSubmissionEnabled,
    isEvaluated,
    isSubmitted,
    isWorksheetEvaluated,
    setQuestionRef,
    createQuestion,
    isFromLibrary,
    portalType,
    showWorksheetAssistant,
    disableScrollEvent,
    activeQuestion,
    isStudentWorkbookForceViewMode,
    assignmentId,
    showAssessmentToolsInQuestions,
    studentAssignmentId,
    isQueriesV2Enabled,
    isLockScreenEnabled,
  } = props;
  const { t } = useI18n(["common", "worksheet"]);

  //WORKSHEET_RESTRUCTURING: Use getFormattedSectionFeedDetailsV2 when queriesV2 is enabled
  const _getFormattedSectionFeedDetails = isQueriesV2Enabled
    ? getFormattedSectionFeedDetailsV2
    : getFormattedSectionFeedDetails;

  const sectionData = useMemo(
    () =>
      _getFormattedSectionFeedDetails({
        sections,
        mode,
        submissions,
        isEvaluationApplicable,
        isSharedWithStudent,
        isStudentEvaluationEnabled,
        isTeacherEvaluationEnabled,
        isStudentSubmissionEnabled,
        isEvaluated,
        isSubmitted,
        isWorksheetEvaluated,
        t,
        showAssessmentToolsInQuestions,
      }),
    [
      sections,
      mode,
      submissions,
      isEvaluationApplicable,
      isSharedWithStudent,
      isStudentEvaluationEnabled,
      isTeacherEvaluationEnabled,
      isStudentSubmissionEnabled,
      isEvaluated,
      isSubmitted,
      isWorksheetEvaluated,
      t,
      showAssessmentToolsInQuestions,
      _getFormattedSectionFeedDetails,
    ]
  );

  // Flag: New student middle-pane revamp (applies only outside teacher create and non-library)
  const isStudentRevamp = useMemo(
    () => isStudentEndMode(mode) && !isFromLibrary,
    [mode, isFromLibrary]
  );

  const addQuestion = useCallback(
    async ({ key, putAfterQuestion, sectionId }) => {
      const organizationResources = [
        {
          type: ORG_RESOURCE_TYPE_WORKSHEET,
          resourceType: RESOURCE_TYPE_QUESTION,
        },
      ];

      await createQuestion({
        parentResourceId: sectionId,
        questionTypeId: key,
        organizationResources,
        putAfterItemId: putAfterQuestion,
      });
    },
    [createQuestion]
  );

  const feedContainerClasses = classNames(classes.feedContainer, {
    [classes.feedViewContainer]: mode != MODE_TEACHER_CREATE,
    [classes.feedContainerRevamp]: isStudentRevamp,
  });

  const containerClasses = classNames(classes.container, {
    [classes.viewContainer]: mode != MODE_TEACHER_CREATE && !isFromLibrary,
    [classes.revampViewContainer]: isStudentRevamp && !isFromLibrary,
    [classes.importFromLibraryContainer]: isFromLibrary,
  });

  return (
    <div className={feedContainerClasses}>
      {_.map(sections, section => {
        let sectionId, sectionLabel;
        // If queries v2 is enabled, we get the section id and label from the item
        // Otherwise, we get it from the section object directly
        if (isQueriesV2Enabled) {
          sectionId = _.get(section, "item.id");
          sectionLabel = _.get(
            _.find(_.get(section, "item.fields", []), {
              uid: FIELD_UID_TITLE,
            }),
            "value",
            null
          );
        } else {
          //WORKSHEET_RESTRUCTURING: clean up section id and label extraction
          sectionId = _.get(section, "id");
          sectionLabel = _.get(section, "label", "");
        }
        const questions = _.get(sectionData, [sectionId, "questions"], []);
        const sectionScore = _.get(sectionData, [sectionId, "score"], 0);
        const sectionMarks =
          isSubmitted || isWorksheetEvaluated
            ? _.get(sectionData, [sectionId, "marks"], 0)
            : "-";

        return (
          <Section
            sectionId={sectionId}
            containerClasses={containerClasses}
            mode={mode}
            isFromLibrary={isFromLibrary}
            sectionLabel={sectionLabel}
            questions={questions}
            addQuestion={addQuestion}
            setQuestionRef={setQuestionRef}
            sections={sections}
            portalType={portalType}
            showWorksheetAssistant={showWorksheetAssistant}
            activeQuestion={activeQuestion}
            disableScrollEvent={disableScrollEvent}
            sectionScore={sectionScore}
            sectionMarks={sectionMarks}
            isStudentWorkbookForceViewMode={isStudentWorkbookForceViewMode}
            assignmentId={assignmentId}
            studentAssignmentId={studentAssignmentId}
            isStudentRevamp={isStudentRevamp}
            isLockScreenEnabled={isLockScreenEnabled}
          />
        );
      })}
    </div>
  );
});

const mapActionCreators = {
  createQuestion,
  disableScrollEvent,
};

const mapStateToProps = state => {
  const activeQuestion = _.get(state, "worksheet.activeQuestion");
  return {
    activeQuestion,
  };
};

SectionFeed.propTypes = {
  sections: PropTypes.arrayOf(PropTypes.object),
  mode: PropTypes.oneOf(WORKSHEET_MODES),
  submissions: PropTypes.arrayOf(PropTypes.object),
  isEvaluationApplicable: PropTypes.bool,
  setQuestionRef: PropTypes.func,
  isFromLibrary: PropTypes.bool,
  activeQuestion: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  studentWorkbookViewMode: PropTypes.bool,
  assignmentId: PropTypes.string,
  showAssessmentToolsInQuestions: PropTypes.bool,
  isQueriesV2Enabled: PropTypes.bool,
};

SectionFeed.defaultProps = {
  sections: [],
  submissions: [],
  mode: MODE_TEACHER_VIEW,
  isEvaluationApplicable: false,
  setQuestionRef: _.noop,
  showAssessmentToolsInQuestions: false,
  isQueriesV2Enabled: false,
};

export default connect(mapStateToProps, mapActionCreators)(SectionFeed);
SectionFeed.displayName = "SectionFeed";
